/*
  Origin carousel
  Scrolling is native (CSS scroll-snap). This element only adds:
    - a `data-overflowing` attribute when the slides are wider than the track, which reveals the arrows
    - previous/next buttons that move one slide at a time and disable at either end
    - scrolling a block into view when it's selected in the theme editor

  Markup:
    <origin-carousel>
      <ul data-origin-carousel-track>...</ul>
      <button data-origin-carousel-prev>…</button>
      <button data-origin-carousel-next>…</button>
    </origin-carousel>
*/
if (!customElements.get('origin-carousel')) {
  class OriginCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-origin-carousel-track]');
      if (!this.track) return;

      this.prevButton = this.querySelector('[data-origin-carousel-prev]');
      this.nextButton = this.querySelector('[data-origin-carousel-next]');
      this.update = this.update.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);

      this.prevButton?.addEventListener('click', () => this.go(-1));
      this.nextButton?.addEventListener('click', () => this.go(1));
      this.track.addEventListener('scroll', this.update, { passive: true });

      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.track);

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      this.update();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    get behavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    /* Width of one slide plus the gap, so each click moves exactly one card */
    get step() {
      const slide = this.track.firstElementChild;
      if (!slide) return this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return slide.getBoundingClientRect().width + gap;
    }

    go(direction) {
      this.track.scrollBy({ left: direction * this.step, behavior: this.behavior });
    }

    update() {
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const overflowing = maxScroll > 1;

      this.toggleAttribute('data-overflowing', overflowing);
      if (this.prevButton) this.prevButton.disabled = !overflowing || this.track.scrollLeft <= 1;
      if (this.nextButton) this.nextButton.disabled = !overflowing || this.track.scrollLeft >= maxScroll - 1;
    }

    onBlockSelect(event) {
      if (!this.track.contains(event.target)) return;
      // The track is position: relative, so offsetLeft is measured from its edge
      const padding = parseFloat(getComputedStyle(this.track).scrollPaddingInlineStart) || 0;
      const left = event.target.offsetLeft - padding;
      this.track.scrollTo({ left, behavior: this.behavior });
    }
  }

  customElements.define('origin-carousel', OriginCarousel);
}
